/// <reference path="../interface.d.ts" />
import { v4 as uuidv4 } from "uuid";
import { backendUrl } from "config";
import { toMerchantIdArray } from "utils";

// ============================================================
// Shared price / number utilities
// ============================================================

/**
 * แปลงค่าใดๆ เป็นตัวเลขที่ finite (ถ้าไม่ได้ return 0)
 * รองรับทั้ง string ที่มี , และ number
 */
export const parsePrice = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  const cleanValue = String(value).replace(/,/g, "");
  const num = Number(cleanValue);
  return isFinite(num) ? num : 0;
};

/**
 * แปลงค่าใดๆ เป็น finite number หรือ 0 (alias สั้นๆ)
 */
export const toFiniteNumberOr0 = (v: any): number => {
  const n = typeof v === "string" ? Number(v.replace(/,/g, "").trim()) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ============================================================
// แสดงเลขแบบมีคอมม่า (หลักพัน) สำหรับทุกช่องราคา/คอมมิชชั่น
// ============================================================

export const formatWithCommas = (value?: string | number): string => {
  if (value === undefined || value === null) return "";
  const str = String(value);
  if (!str) return "";

  const [intPart, decPart] = str.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
};

/**
 * ใช้สำหรับ "Payment Gateway" เท่านั้น: บังคับแสดงทศนิยม 2 ตำแหน่ง (เช่น 12.30)
 */
export const formatWithCommasFixed2 = (value?: string | number): string => {
  if (value === undefined || value === null) return "";
  const num = typeof value === "number" ? value : Number(String(value).replace(/,/g, "").trim());
  if (!Number.isFinite(num)) return "";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * parser ของ AntD InputNumber (ในโปรเจกต์นี้) ต้อง return เป็น number
 * - ถ้าเป็นค่าว่าง ให้ return NaN เพื่อคงสภาพ "ว่าง" (จะถูกจัดการตอน blur ตาม logic เดิม)
 */
export const parseNumberWithoutCommas = (value?: string): number => {
  if (value === undefined) return Number.NaN;
  const cleaned = value.replace(/,/g, "").trim();
  if (!cleaned) return Number.NaN;
  const num = Number(cleaned);
  return Number.isNaN(num) ? Number.NaN : num;
};

/**
 * ฟังก์ชันสำหรับตัดเลข 0 ด้านหน้าและจำกัดความยาว
 * @param value - ค่าที่ต้องการประมวลผล
 * @param maxLength - ความยาวสูงสุด (default: 10)
 * @returns ค่าที่ประมวลผลแล้ว
 */
export const removeLeadingZeros = (value: string, maxLength: number = 10): string => {
  let processedValue = value.replace(/^0+/, '') || '0';
  if (processedValue.length > maxLength) {
    processedValue = processedValue.slice(0, maxLength);
  }
  return processedValue;
};

// ============================================================
// Speed Shipping helpers
// ============================================================

/**
 * แปลง delivery_time จาก API: "day/days" -> "วัน" (แสดงผลเท่านั้น ไม่แก้ข้อมูลดิบ)
 */
export const formatDeliveryTimeTh = (value?: string | null): string => {
  if (!value) return "";
  return String(value).replace(/\bdays?\b/gi, "วัน").trim();
};

/**
 * requirement: logo ขนส่งที่ดึงมาจาก api speed ให้ตัดคำว่า "_SPEED" ออก
 */
export const normalizeSpeedLogo = (logo: any): string | undefined => {
  if (logo === null || logo === undefined) return undefined;
  const s = String(logo).replace(/_SPEED/gi, "").trim();
  return s ? s : undefined;
};

/**
 * ใช้สำหรับ sort กรณีราคาเท่ากัน: เอาระยะเวลาส่งที่ "น้อยกว่า" ขึ้นก่อน
 * รองรับรูปแบบเช่น: "1 - 5 days", "1 - 30 วัน", "Next day"
 */
export const parseDeliveryMinDays = (value?: string): number => {
  if (!value) return Number.POSITIVE_INFINITY;
  const s = String(value).toLowerCase();
  if (s.includes("next")) return 1;
  const m = s.match(/(\d+)/);
  if (m?.[1]) {
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
  }
  return Number.POSITIVE_INFINITY;
};

export const parseTotal = (v: any): number => {
  const num = Number(typeof v === "string" ? v.replace(/,/g, "") : v);
  return Number.isFinite(num) ? num : 0;
};

/**
 * ดึงชุดราคาขนส่งชื่อตรง DB จาก response check-price (รองรับ alias เดิม)
 * — ไม่ปัดซ้ำฝั่ง FE (BE ปัดใน applyAddOn แล้ว)
 */
export const pickTransportBreakdown = (src: {
  api_transport_price?: number;
  add_on_api_transport_price?: number;
  transport_price?: number;
  add_on_price?: number;
  total_with_add_on?: number;
  price_with_add_on?: number;
  total?: number | string;
  price?: number | string;
} | null | undefined) => {
  if (!src) {
    return { api_transport_price: 0, add_on_api_transport_price: 0, transport_price: 0 };
  }
  const api_transport_price = Number(
    src.api_transport_price ??
      (src.total != null ? parseTotal(src.total) : undefined) ??
      (src.price != null ? parseTotal(src.price) : undefined) ??
      0,
  );
  const transport_price = Number(
    src.transport_price ?? src.total_with_add_on ?? src.price_with_add_on ?? api_transport_price,
  );
  const add_on_api_transport_price = Number(
    src.add_on_api_transport_price ?? src.add_on_price ?? transport_price - api_transport_price,
  );
  return { api_transport_price, add_on_api_transport_price, transport_price };
};

// ============================================================
// API Payload builders (ใช้ร่วมกันระหว่าง edit/create mode ใน index.tsx)
// ============================================================

interface BuildFormattedPriceParams {
  editMode: boolean;
  editData?: { variant: Variant; price: Price; index: number } | null;
  itemId: string;
  newPrice: VariationPrice;
}

/**
 * สร้าง formatted price object สำหรับส่ง API
 * ใช้ร่วมกันระหว่าง editMode + !editMode
 */
export const buildFormattedPrice = ({
  editMode,
  editData,
  itemId,
  newPrice,
}: BuildFormattedPriceParams) => ({
  ...(editMode && editData?.price?.id ? { id: editData.price.id } : {}),
  variant_1_item_id: itemId,
  variant_2_item_id: newPrice.variant_2_item_id || "",
  factory_price: newPrice.factory_price,
  wholesale_price: newPrice.wholesale_price,
  retail_price: newPrice.retail_price,
  vip_price: newPrice.vip_price,
  base_factory_price: newPrice.base_factory_price,
  base_vip_price: newPrice.base_vip_price,
  base_wholesale_price: newPrice.base_wholesale_price,
  base_retail_price: newPrice.base_retail_price,
  transport_price: newPrice.transport_price,
  api_transport_price: newPrice.api_transport_price,
  api_transport_carrier_code: newPrice.api_transport_carrier_code,
  api_transport_logo: newPrice.api_transport_logo,
  add_on_api_transport_price: newPrice.add_on_api_transport_price ?? 0,
  percent_payment_gateway: newPrice.percent_payment_gateway,
  payment_getway: newPrice.payment_getway ?? 0,
  commistion_lv1: newPrice.commistion_lv1 ?? 0,
  commistion_lv2: newPrice.commistion_lv2 ?? 0,
  commistion_lv3: newPrice.commistion_lv3 ?? 0,
  commistion_lv4: newPrice.commistion_lv4 ?? 0,
  commistion_lv5: newPrice.commistion_lv5 ?? 0,
  total_commission: newPrice.total_commission ?? 0,
  unit_factory_price: newPrice.unit_factory_price || "0",
  unit_wholesale_price: newPrice.unit_wholesale_price || "0",
  unit_retail_price: newPrice.unit_retail_price || "0",
  unit_vip_price: newPrice.unit_vip_price || "0",
  quantity_per_unit: (newPrice as any).quantity_per_unit ?? 0,
  product_unit: newPrice.product_unit || null,
  self_transport_price: newPrice.self_transport_price ?? 0,
  include_transport_price: (newPrice as any).include_transport_price !== false,
  self_transport_carrier_id: (newPrice as any).self_transport_carrier_id ?? null,
});

interface BuildSingleItemDtoParams {
  editMode: boolean;
  editData?: { variant: Variant; price: Price; index: number } | null;
  singleItem: any;
  formattedPrice: ReturnType<typeof buildFormattedPrice>;
  getDefaultMerchantCodes: () => string[];
}

/**
 * สร้าง single item DTO สำหรับส่ง API
 * ใช้ร่วมกันระหว่าง editMode + !editMode
 */
export const buildSingleItemDto = ({
  editMode,
  editData,
  singleItem,
  formattedPrice,
  getDefaultMerchantCodes,
}: BuildSingleItemDtoParams) => ({
  ...(editMode && editData ? { id: singleItem.id } : { id: singleItem.id }),
  name: singleItem.name,
  product_capacity: singleItem.product_capacity,
  product_quantity: singleItem.product_quantity,
  lead_time: singleItem.lead_time,
  preparation_time: singleItem.preparation_time, // ✅ ส่ง preparation_time ไปบันทึก
  product_code: singleItem.product_code,
  image_id: singleItem.image_id,
  image_url: singleItem.image_id ? `${backendUrl}images/${singleItem.image_id}` : "",
  width: singleItem.width,
  length: singleItem.length,
  height: singleItem.height,
  weight: singleItem.weight,
  shipping_method: singleItem.shipping_method || "self",
  is_cod: singleItem.is_cod ?? false,
  is_active: singleItem.is_active,
  is_draft: singleItem.is_draft ?? false,
  partner_transport_id: singleItem.partner_transport_id || "",
  partner_transport_brand_id: singleItem.partner_transport_brand_id || "",
  packaging_type_id: singleItem.packaging_type_id || null,
  merchants:
    singleItem.merchants && singleItem.merchants.length > 0
      ? toMerchantIdArray(singleItem.merchants)
      : getDefaultMerchantCodes(),
  // merchant_id:
  //   singleItem.merchant_id ||
  //   factoryData?.factory?.merchants?.find((m: { is_default: boolean }) => m.is_default)?.merchant_id ||
  //   "",
  // ✅ ส่ง sku_tag_ids ขึ้น API (ปัจจุบันใช้กลุ่ม sale_channel: wholesale/retail) — backend จะ sync product_sku_tag_relation
  sku_tag_ids: Array.isArray(singleItem.sku_tag_ids) ? singleItem.sku_tag_ids : [],
  price: formattedPrice,
});

/**
 * ฟังก์ชันสำหรับตรวจสอบว่า field ที่เปลี่ยนแปลงเป็นราคาตั้งต้น ค่าขนส่ง หรือคอมมิชชั่นหรือไม่
 * ใช้สำหรับตัดสินใจว่าควรคำนวณราคาสุดท้ายใหม่หรือไม่
 */
export const isBasePriceOrTransportField = (changedField: string): boolean => {
  const basePriceFields = [
    "base_factory_price",
    "base_vip_price",
    "base_wholesale_price",
    "base_retail_price",
    "transport_price",
    "self_transport_price",
    "include_transport_price",
    // ✅ ไม่ใช้ c1-c3 ในการคำนวณแล้ว → ไม่ต้อง trigger การคำนวณจากฟิลด์เหล่านี้
    "total_commission", // ✅ เพิ่ม: ค่าคอมมิชชั่นรวม
    "input_market_price", // ✅ เปลี่ยนจาก retail_price: เมื่อ input_market_price (ราคาตลาดด้านบน) เปลี่ยน → trigger คำนวณ commission + base_factory_price + factory_price
  ];
  return basePriceFields.includes(changedField);
};

/**
 * คำนวณ base_factory_price ตามสูตร "ทุนส่งให้แบ่งเบา = แนะนำขาย × 80%" (ตรงๆ)
 *
 * สูตรใหม่ (หลังปรับโครงสร้างราคา — ทุนส่งให้แบ่งเบา = แนะนำขาย 80% ตรงๆ):
 *   base_factory_price = retail_price × 0.80
 *
 * สูตรเก่า (reverse-calc หัก S/C/G) — เก็บไว้เป็นประวัติ ห้ามลบ:
 *   target_factory_price = retail_price × 0.75  (ต่อมาเปลี่ยนเป็น 0.80)
 *   total_commission = retail_price × percentCommission / 100
 *   base_factory_price = (target / (1 + paymentGatewayPercent)) - transport - commission
 *
 * หมายเหตุ: คง signature เดิมเพื่อไม่กระทบ caller; พารามิเตอร์ percentCommission,
 * paymentGatewayPercent, transportPrice ไม่ได้ใช้ในสูตรใหม่แล้ว (คงไว้เพื่อ backward-compat)
 *
 * @param retailPrice - ราคาตลาด / ราคาแนะนำขาย (ผู้ใช้กรอก)
 * @param percentCommission - % คอมมิชชั่นจากประเภทสินค้า (เช่น 15) — ไม่ใช้แล้วในสูตรใหม่
 * @param paymentGatewayPercent - % Payment Gateway เป็นทศนิยม (เช่น 0.03) — ไม่ใช้แล้วในสูตรใหม่
 * @param transportPrice - ค่าขนส่ง (0 ถ้าส่งเอง) — ไม่ใช้แล้วในสูตรใหม่
 * @returns base_factory_price ที่คำนวณได้ (ไม่ต่ำกว่า 0) = retail_price × 0.80
 */
export const reverseCalculateBasePrice = (
  retailPrice: number,
  percentCommission: number,      // ไม่ใช้แล้วในสูตรใหม่ — คงไว้เพื่อ backward-compat
  paymentGatewayPercent: number,  // ไม่ใช้แล้วในสูตรใหม่ — คงไว้เพื่อ backward-compat
  transportPrice: number          // ไม่ใช้แล้วในสูตรใหม่ — คงไว้เพื่อ backward-compat
): number => {
  // ✅ สูตรใหม่: ทุนส่งให้แบ่งเบา = แนะนำขาย × 80% (ตรงๆ ไม่หัก S/C/G)
  const base = round2(retailPrice * 0.80);
  return Math.max(0, base);
};

// ============================================================
// Category helpers
// ============================================================

/**
 * ค้นหา CategoryData จาก nested tree แบบ recursive
 * ใช้สำหรับหา percent_commission และ name จาก category_id ที่ผู้ใช้เลือก
 * @param categories - array ของ CategoryData (อาจมี childrens ซ้อนกัน)
 * @param id - category_id ที่ต้องการค้นหา
 * @returns CategoryData ที่ตรงกับ id หรือ undefined ถ้าไม่เจอ
 */
export const findCategoryById = (
  categories: { id: string; name: string; percent_commission: number; childrens?: any[] }[],
  id: string
): { id: string; name: string; percent_commission: number; childrens?: any[] } | undefined => {
  if (!categories || !id) return undefined;
  for (const cat of categories) {
    if (cat.id === id) return cat;
    if (cat.childrens && cat.childrens.length > 0) {
      const found = findCategoryById(cat.childrens, id);
      if (found) return found;
    }
  }
  return undefined;
};

// ============================================================
// Commission calculation helpers
// ============================================================

/**
 * ปัด 2 ตำแหน่งแบบปลอดภัย
 */
export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * คำนวณ commission levels จาก total_commission
 * auto-fill ตามสัดส่วน: C1(ผู้แนะนำร้าน)=2%, C2(ดูแลผู้แนะนำ)=25%, C3(ผู้แนะนำ)=60%, C4(ผู้ซื้อ)=10%, C5(การตลาด)=~3% (remainder)
 * ให้ C5 เป็น remainder เพื่อให้ผลรวมเท่ากับ total_commission เสมอ
 */
export const calculateCommissionLevels = (totalCommission: number) => {
  const totalInCents = Math.round(totalCommission * 100);
  const c1InCents = Math.round(totalInCents * 0.02);  // ผู้แนะนำร้าน 2%
  const c2InCents = Math.round(totalInCents * 0.25);  // ดูแลผู้แนะนำ 25%
  const c3InCents = Math.round(totalInCents * 0.60);  // ผู้แนะนำ 60%
  const c4InCents = Math.round(totalInCents * 0.10);  // ผู้ซื้อ 10%
  // ✅ C5(การตลาด) เป็น remainder (3%) เพื่อให้ผลรวมเท่ากับ total_commission เสมอ
  const c5InCents = totalInCents - c1InCents - c2InCents - c3InCents - c4InCents;

  let c1 = round2(c1InCents / 100);
  let c2 = round2(c2InCents / 100);
  let c3 = round2(c3InCents / 100);
  let c4 = round2(c4InCents / 100);
  let c5 = round2(c5InCents / 100);

  // ✅ ตรวจสอบผลรวมและปรับ C5 ให้ถูกต้อง (ป้องกัน floating point errors)
  const sum = round2(c1 + c2 + c3 + c4 + c5);
  const diff = round2(totalCommission - sum);
  if (Math.abs(diff) > 0.001) {
    c5 = round2(c5 + diff);
  }

  return { c1, c2, c3, c4, c5 };
};
